// Кнопка «Выгрузить в Excel»: одна на все отчёты.
//
// Жила в экране сводки, переехала сюда, когда выгрузок стало две. Копия рядом с копией
// разъезжается на первой же правке — а половина смысла этой кнопки в тексте отказа, который
// объясняет, чего человеку не хватает, вместо серой неработающей кнопки.

import { useState } from 'react';

import { RiDownload2Line } from '@remixicon/react';

import type { FileResponse } from '@/shared/api/client';
import { сохранитьФайл } from '@/shared/desktop';
import { текстОшибки } from '@/shared/reports/текст-ошибки';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import * as Button from '@/shared/ui/button';
import * as Hint from '@/shared/ui/hint';
import * as Toast from '@/shared/ui/toast';

/** Право на выгрузку отдельное: файл уносят из системы, и он переживает отзыв доступа. */
const EXPORT_PERMISSION = 'core.report.export';

export function ExportButton({
  собрать,
  что,
  требуетПраво = true,
}: {
  собрать: () => Promise<FileResponse>;
  /**
   * Что именно выгружается: «расчёт», «недобор». Подставляется в подпись.
   *
   * Нужно там, где на ОДНОМ экране две выгрузки. В сводке за месяц их две — по расчёту и по
   * недобору людей, — и обе назывались «Выгрузить в Excel»: на снимке 26.08.2026 это две
   * одинаковые кнопки, между которыми человек ищет разницу. Где выгрузка одна, подпись
   * остаётся общей: лишнее слово там ничего не добавляет.
   */
  что?: string;
  /**
   * false — выгрузка не требует права на отчёты.
   *
   * Так устроен СВОЙ расчётный лист: это собственные деньги человека, и сервер права на
   * отчёты по компании тут не спрашивает. Кнопка, требующая его на клиенте, отказывала бы
   * охраннику в его же листе — там, где сервер отдал бы файл.
   */
  требуетПраво?: boolean;
}) {
  const права = useAuthStore((state) => state.permissions);
  const можно = !требуетПраво || can(права, EXPORT_PERMISSION);
  const [идёт, setИдёт] = useState(false);
  const [запрет, setЗапрет] = useState<string | null>(null);
  const { addToast } = Toast.useToast();

  async function выгрузить() {
    // Кнопка не сереет, а объясняет: выключенная кнопка не доводит нажатие до обработчика,
    // человек жмёт и не получает ничего — со стороны это неотличимо от сломанной программы.
    if (!можно) {
      setЗапрет(
        'Выгрузку открывает право на отчёты. Попросите администратора выдать его — либо попросите файл у того, у кого право есть.',
      );
      return;
    }
    if (идёт) {
      setЗапрет('Файл уже собирается на сервере. Второй запрос не отправляем.');
      return;
    }
    setЗапрет(null);
    setИдёт(true);
    try {
      const файл = await собрать();
      // В окне программы файл сохраняется диалогом, в браузере — скачиванием. Разница
      // спрятана в общем слое: ссылка с `download` внутри webview не работает, и без этой
      // ветки кнопка в поставленной программе молча ничего бы не делала.
      const сохранено = await сохранитьФайл(файл.blob, файл.filename);
      if (!сохранено) {
        setЗапрет('Место не выбрано — файл не сохранён. Нажмите ещё раз, если он нужен.');
        return;
      }
      // Удача была НЕМОЙ, и это единственное действие продукта, где немота опаснее всего:
      // отказ выгрузки объяснялся словами, а успех не сообщал ничего. Человек нажимал,
      // подпись на кнопке возвращалась с «Собираем файл…» на прежнюю — и всё. В окне
      // программы файл при этом уходит в выбранную папку, а не в «Загрузки» браузера, то есть
      // человек даже не видит полоски скачивания. Он жмёт второй раз.
      //
      // Имя файла в подтверждении обязательно: выгрузок на экране бывает две, и «готово» без
      // имени не отвечает, какая из них готова.
      addToast({
        variant: 'success',
        title: 'Файл сохранён',
        description: файл.filename,
      });
    } catch (ошибка) {
      // Провал выгрузки нельзя проглатывать: человек нажал, ничего не скачалось, и он не
      // знает — то ли файл готовится, то ли сервер отказал. Молчание тут читается как
      // поломка всей программы.
      const текст = текстОшибки(ошибка, 'сервер не отдал файл');
      setЗапрет(`Не выгрузилось: ${текст}. Повторите — если не поможет, скажите администратору.`);
    } finally {
      setИдёт(false);
    }
  }

  // Кнопка НЕ выравнивает себя сама. Пока здесь стояло `items-end`, она прижималась к правому
  // краю любого столбца, в который её положили: в отборах пульта подотчёта все поля шли по
  // левому краю, а кнопка выгрузки одна висела справа — ровно то, что владелец назвал
  // «кривовато». Куда встать, решает тот, кто кладёт, а не сама кнопка.
  return (
    <div className='flex flex-col items-start gap-1'>
      <Button.Root
        type='button'
        variant='neutral'
        mode='stroke'
        size='xsmall'
        onClick={() => void выгрузить()}
      >
        <Button.Icon as={RiDownload2Line} />
        {идёт ? 'Собираем файл…' : что === undefined ? 'Выгрузить в Excel' : `Выгрузить ${что}`}
      </Button.Root>
      {запрет === null ? null : <Hint.Root>{запрет}</Hint.Root>}
    </div>
  );
}
